/**
 * 서명 편집 다이얼로그 — 이름 + 작성 방식(기본 / HTML) + 본문.
 *
 *  initial:
 *    null/undefined  — 닫힘
 *    {}              — 새 서명 작성 모드
 *    { id, name, bodyType, bodyText, bodyHtml, isDefault } — 기존 서명 수정 모드
 *
 * ## 작성 방식 두 가지
 *   기본(TEXT) — 친 글자가 곧 결과. `<b>` 를 치면 굵은 글씨가 아니라 `<b>` 라는 글자가 보인다.
 *                줄바꿈만 살아난다. 서명 대부분(이름/직급/연락처)은 이걸로 충분하고,
 *                붙여넣기 과정에서 엉뚱한 스타일이 딸려 들어오는 사고가 없다.
 *   HTML       — 마크업 직접 입력. 로고·표·인라인 스타일이 필요한 대외용 서명용.
 *                아래쪽 미리보기로 렌더 결과를 즉시 확인한다.
 *                이미지는 "이미지 넣기" 로 base64 <img> 를 커서 위치에 끼워 넣는다 (handleUploadImage 주석 참고).
 *
 * ## 방식 전환 시 본문 처리
 *   기본 → HTML : 친 텍스트를 escape + `<br>` 로 바꿔 소스 칸에 옮겨 준다 (내용 보존).
 *   HTML → 기본 : 태그가 전부 사라지므로 confirm 을 거친다. 승낙 시 태그를 벗긴 텍스트만 남긴다.
 *
 * ## 서버가 최종 결정권을 가진다
 *   여기서 만든 HTML 은 편의일 뿐, 저장 시 서버(MailSignatureService.applyBody)가
 *   방식에 맞춰 bodyHtml 을 다시 확정한다. HTML 방식은 새니타이즈를 거치므로
 *   저장 후 다시 열면 정제된 소스가 보인다 — 의도된 동작.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Box, Button, FormControlLabel, Stack, Switch, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from "@mui/material";
import ImageRoundedIcon from "@mui/icons-material/ImageRounded";
import DOMPurify from "dompurify";

import { mailApi } from "../../api/mailApi";
import { notify }  from "../../../../util/toast";
import { confirm } from "../../../../util/confirmDialog";
import FormModal   from "../../../../component/common/FormModal";
import { pinImageBoxes } from "./pinImageBoxes";

const TEXT_PLACEHOLDER = "OOO / 주임\nOnnme Inc.\n010-0000-0000";
const HTML_PLACEHOLDER = '<div style="font-size:13px; color:#334155;">\n  <b>OOO</b> / 주임<br/>\n  Onnme Inc.\n</div>';

/** 서명에 박을 이미지 상한 — body_html(MySQL TEXT, 64KB)에 base64 로 들어가므로. */
const MAX_IMAGE_BYTES = 40 * 1024;

/** 텍스트 → HTML. 서버(MailSignatureService.textToHtml)와 같은 규칙이라 미리보기가 저장 결과와 일치한다. */
const textToHtml = (text) => {
    if (!text || !text.trim()) return "";
    const escaped = String(text)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
    return `<p>${escaped.replace(/\r\n/g, "\n").replace(/\n/g, "<br/>")}</p>`;
};

/**
 * HTML → 텍스트 (HTML 방식 → 기본 방식 전환용).
 *
 * DOMParser 로 파싱하는 이유: 문자열을 살아있는 DOM(innerHTML)에 넣으면 `<img onerror=...>` 같은
 * 게 실제로 실행될 수 있다. DOMParser 가 만든 문서는 비활성(inert)이라 스크립트도 리소스 로딩도 없다.
 */
const htmlToText = (html) => {
    if (!html) return "";
    const doc = new DOMParser().parseFromString(String(html), "text/html");
    doc.body.querySelectorAll("br").forEach((br) => br.replaceWith("\n"));
    doc.body.querySelectorAll("p, div, li, tr, h1, h2, h3, h4, h5, h6")
        .forEach((el) => el.append("\n"));
    return (doc.body.textContent || "")
        .replace(/ /g, " ")       // &nbsp; → 보통 공백
        .replace(/[ \t]+\n/g, "\n")
        .replace(/\n{3,}/g, "\n\n")
        .trim();
};

export default function SignatureEditorDialog({ open, onClose, initial, onSaved }) {
    const [name, setName]           = useState("");
    const [bodyType, setBodyType]   = useState("TEXT");
    const [bodyText, setBodyText]   = useState("");
    const [bodyHtml, setBodyHtml]   = useState("");
    const [isDefault, setIsDefault] = useState(false);
    const [saving, setSaving]       = useState(false);
    const [uploading, setUploading] = useState(false);

    const htmlFieldRef = useRef(null);   // <img> 태그를 커서 위치에 끼워 넣기 위해
    const fileInputRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        setName(initial?.name || "");
        // 신규는 기본(TEXT). 기존 서명은 서버가 준 방식 그대로 — bodyType 이 없는 레거시 행은
        // 전부 Tiptap HTML 이므로 HTML 로 연다 (TEXT 로 열면 마크업이 통째로 날아간다).
        setBodyType(initial?.bodyType || (initial?.id ? "HTML" : "TEXT"));
        setBodyText(initial?.bodyText || "");
        setBodyHtml(initial?.bodyHtml || "");
        setIsDefault(Boolean(initial?.isDefault));
    }, [open, initial]);

    const isEdit = Boolean(initial?.id);
    const isHtml = bodyType === "HTML";

    /**
     * 미리보기 — 저장 후 실제로 박힐 HTML 과 같은 것을 보여 준다.
     * pinImageBoxes 까지 통과시키는 이유: 작성기에 삽입될 때도 같은 처리를 거치므로
     * (ComposeModal.prepareSignatureHtml) 여기서 빼면 로고가 깨졌을 때 이 미리보기만
     * 열 폭이 다르게 보인다.
     */
    const previewHtml = useMemo(() => {
        const raw = isHtml ? pinImageBoxes(bodyHtml) : textToHtml(bodyText);
        return DOMPurify.sanitize(raw || "", { ADD_ATTR: ["target", "rel"] });
    }, [isHtml, bodyHtml, bodyText]);

    /**
     * 방식 전환. 내용을 최대한 옮기되, 되돌릴 수 없는 손실(HTML → 기본)만 확인을 받는다.
     */
    const handleChangeType = async (next) => {
        if (!next || next === bodyType) return;

        if (next === "HTML") {
            // 텍스트를 소스로 승격 — 지금까지 친 내용이 사라지지 않게.
            if (bodyText.trim() && !bodyHtml.trim()) setBodyHtml(textToHtml(bodyText));
            setBodyType("HTML");
            return;
        }

        // HTML → 기본: 태그가 전부 사라진다. 내용이 있을 때만 물어본다.
        if (bodyHtml.trim()) {
            const ok = await confirm({
                title: "기본 방식으로 전환",
                message: "HTML 태그·이미지·스타일이 모두 사라지고 글자만 남습니다. 계속할까요?",
                confirmLabel: "전환",
                variant: "danger",
            });
            if (!ok) return;
            setBodyText(htmlToText(bodyHtml));
        }
        setBodyType("TEXT");
    };

    /**
     * 로고 등 이미지 넣기 → base64 data: URL 로 커서 위치에 <img> 삽입.
     *
     * <h4>왜 S3 업로드(mailApi.uploadSignatureImage)를 쓰지 않나</h4>
     * 업로드가 돌려주는 건 `/api/mail/signatures/images/{key}` 상대 URL인데,
     * 백엔드 HtmlSanitizer(Jsoup)는 base URI 없이 clean 하므로 <b>상대 URL src 를 통째로 지운다</b>.
     * 저장 버튼을 누르는 순간 이미지가 사라진다는 뜻. 살아남는 건 http(s)/data/cid 뿐이다.
     * (구 Tiptap 편집기가 업로드 훅에서 null 을 반환해 base64 로 우회하던 것도 같은 이유.)
     *
     * data: 로 박아 두면 이후 흐름이 전부 자동이다 —
     * 작성기에서 즉시 렌더되고, 발송 직전 백엔드 extractInlineImages 가 cid: 인라인 첨부로 바꾼다.
     *
     * <h4>용량 제한</h4>
     * body_html 은 MySQL TEXT(64KB). base64 는 원본의 약 1.37배라 큰 이미지는 저장 시 잘린다.
     * 조용히 깨지느니 미리 막는다. 외부 호스팅 이미지를 절대 URL(https://…)로 넣는 건 제한 없음.
     */
    const handleUploadImage = (file) => {
        if (!file) return;
        if (!file.type?.startsWith("image/")) {
            notify("이미지 파일만 넣을 수 있습니다", "warning");
            if (fileInputRef.current) fileInputRef.current.value = "";
            return;
        }
        if (file.size > MAX_IMAGE_BYTES) {
            notify(`이미지는 ${Math.floor(MAX_IMAGE_BYTES / 1024)}KB 이하만 가능합니다 (선택한 파일 ${Math.ceil(file.size / 1024)}KB).
줄여서 다시 넣거나, 외부 주소를 <img src="https://…"> 로 직접 쓰세요.`, "warning");
            if (fileInputRef.current) fileInputRef.current.value = "";
            return;
        }

        setUploading(true);
        const reader = new FileReader();
        reader.onload = () => {
            const tag = `<img src="${reader.result}" alt="" style="max-width:100%;" />`;
            const el  = htmlFieldRef.current;
            const at  = el && typeof el.selectionStart === "number" ? el.selectionStart : bodyHtml.length;
            setBodyHtml(bodyHtml.slice(0, at) + tag + bodyHtml.slice(at));
            notify("이미지를 넣었습니다", "success");
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = "";  // 같은 파일 재선택 허용
        };
        reader.onerror = () => {
            notify("이미지를 읽지 못했습니다", "error");
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
        };
        reader.readAsDataURL(file);
    };

    const handleSave = async () => {
        if (!name.trim()) { notify("서명 이름을 입력해주세요", "warning"); return; }
        setSaving(true);
        try {
            // 방식에 해당하는 본문만 보낸다 — 서버가 이걸로 bodyHtml 을 확정한다.
            const body = isHtml ? { bodyType: "HTML", bodyHtml } : { bodyType: "TEXT", bodyText };
            if (isEdit) {
                await mailApi.updateSignature(initial.id, { name: name.trim(), ...body });
                if (isDefault && !initial.isDefault) {
                    await mailApi.setDefaultSignature(initial.id);
                }
            } else {
                const res = await mailApi.createSignature({ name: name.trim(), ...body, isDefault });
                const created = res?.data ?? res;
                if (isDefault && created?.id) {
                    try { await mailApi.setDefaultSignature(created.id); } catch { /* noop */ }
                }
            }
            notify("서명을 저장했습니다", "success");
            onSaved?.();
        } catch (e) {
            notify(e?.response?.data?.message || "서명 저장 실패", "error");
        } finally { setSaving(false); }
    };

    return (
        <FormModal
            open={open}
            onClose={onClose}
            title={isEdit ? "서명 편집" : "새 서명"}
            subtitle="여기 작성한 내용이 메일 작성/답장 시 본문 끝에 자동으로 들어갑니다."
            maxWidth="md"
            onSave={handleSave}
            saving={saving}
        >
            <Stack spacing={1.5}>
                <TextField
                    label="서명 이름"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    placeholder="예: 영업용, 외부용 등"
                    fullWidth
                    sx={{ "& .MuiInputBase-root": { fontSize: 13 } }}
                />

                <Stack direction="row" alignItems="center" spacing={1.5}>
                    <ToggleButtonGroup
                        size="small" exclusive
                        value={bodyType}
                        onChange={(_, v) => handleChangeType(v)}
                    >
                        <ToggleButton value="TEXT" sx={{ textTransform: "none", fontWeight: 700, fontSize: 12, px: 2 }}>
                            기본
                        </ToggleButton>
                        <ToggleButton value="HTML" sx={{ textTransform: "none", fontWeight: 700, fontSize: 12, px: 2 }}>
                            HTML
                        </ToggleButton>
                    </ToggleButtonGroup>
                    <Typography sx={{ fontSize: 11, color: "#64748b", lineHeight: 1.5 }}>
                        {isHtml
                            ? "HTML 태그가 그대로 적용됩니다. 스크립트 등 위험한 태그는 저장 시 제거됩니다."
                            : "글자 그대로 들어갑니다. 태그를 쓰려면 HTML 로 전환하세요."}
                    </Typography>
                </Stack>

                {isHtml ? (
                    <>
                        <Box>
                            <Button
                                size="small"
                                startIcon={<ImageRoundedIcon sx={{ fontSize: 16 }} />}
                                onClick={() => fileInputRef.current?.click()}
                                disabled={uploading}
                                sx={{ textTransform: "none", fontSize: 12, fontWeight: 700, color: "#475569" }}
                            >
                                {uploading ? "읽는 중…" : "이미지 넣기"}
                            </Button>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                hidden
                                onChange={(e) => handleUploadImage(e.target.files?.[0])}
                            />
                        </Box>
                        <TextField
                            value={bodyHtml}
                            onChange={(e) => setBodyHtml(e.target.value)}
                            inputRef={htmlFieldRef}
                            multiline
                            minRows={10}
                            maxRows={18}
                            fullWidth
                            placeholder={HTML_PLACEHOLDER}
                            spellCheck={false}
                            InputProps={{
                                sx: {
                                    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
                                    fontSize: 12.5, lineHeight: 1.6, alignItems: "flex-start",
                                    bgcolor: "#f8fafc",
                                },
                            }}
                        />
                        <Box>
                            <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#64748b", mb: 0.5 }}>
                                미리보기
                            </Typography>
                            <Box
                                sx={{
                                    p: 1.5, minHeight: 72, maxHeight: 220, overflow: "auto",
                                    border: "1px solid #e2e8f0", borderRadius: 1, bgcolor: "#fff",
                                    fontSize: 13, color: "#334155",
                                    "& img": { maxWidth: "100%", height: "auto" },
                                }}
                                dangerouslySetInnerHTML={{
                                    __html: previewHtml || '<span style="color:#94a3b8">내용을 입력하면 여기에 표시됩니다.</span>',
                                }}
                            />
                        </Box>
                    </>
                ) : (
                    <TextField
                        value={bodyText}
                        onChange={(e) => setBodyText(e.target.value)}
                        multiline
                        minRows={10}
                        maxRows={18}
                        fullWidth
                        placeholder={TEXT_PLACEHOLDER}
                        InputProps={{
                            sx: { fontSize: 13, lineHeight: 1.7, alignItems: "flex-start" },
                        }}
                    />
                )}

                <FormControlLabel
                    control={<Switch checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} />}
                    label={<Typography sx={{ fontSize: 12 }}>기본 서명으로 설정</Typography>}
                />
            </Stack>
        </FormModal>
    );
}
